import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, LogOut, X, KeyRound, Unlock } from "lucide-react";
import { useAuth } from "../lib/auth";
import { setImpersonationToken } from "../lib/api";

/**
 * Feb 2026 · Persistenter Impersonations-Banner.
 *
 * User-Directive: „Als Admin muss ich zwischen Ebenen togglen können —
 * ADMIN, Concierge-Anblick per Concierge (ohne dass Admin was ändern kann)
 * und Kunden-Ansicht pro Kunde (ohne dass Admin was ändern kann)."
 *
 * Der Banner zeigt permanent an, dass die aktuelle Ansicht schreibgeschützt
 * ist, und bietet einen One-Klick-Exit zurück zur Admin-Session.
 *
 *  - Concierge-Ansicht (gleicher Tab):   „Zurück zu Admin" setzt effectiveRole
 *                                        auf `admin` und navigiert zu /team.
 *  - Kunden-Ansicht  (neuer Tab):        „Ansicht beenden" ruft /auth/logout
 *                                        auf (schließt das
 *                                        customer_impersonation-Cookie)
 *                                        und schließt den Tab.
 */
export default function ImpersonationBanner() {
  const nav = useNavigate();
  const {
    user,
    isImpersonatingConcierge,
    isImpersonatingCustomer,
    activeConciergeName,
    customerImpersonation,
    setEffectiveRole,
    setImpersonatedConcierge,
    otherConciergeUnlocked,
    requestPinPrompt,
    lockOtherConcierge,
  } = useAuth();

  const exitConcierge = useCallback(() => {
    setImpersonatedConcierge(null);
    setEffectiveRole("admin");
    nav("/team");
  }, [nav, setEffectiveRole, setImpersonatedConcierge]);

  const exitCustomer = useCallback(async () => {
    // Nur den Tab-Token verwerfen — KEIN /auth/logout, sonst löscht der
    // Server den Admin-Cookie für den ganzen Browser (Bug „Not authenticated").
    setImpersonationToken(null);
    // Versuche den Tab zu schließen — bei script-opened tabs erlaubt der
    // Browser das. Fallback: /login redirect.
    try {
      window.close();
    } catch { /* ignore */ }
    // Falls window.close nicht greift (nicht per Script geöffnet):
    setTimeout(() => {
      window.location.href = "/login";
    }, 150);
  }, []);

  const payload = useMemo(() => {
    if (isImpersonatingCustomer) {
      return {
        kind: "customer",
        title: `Kundenansicht als ${user?.name || user?.email || "Kunde"}`,
        subtitle: customerImpersonation?.by_name || customerImpersonation?.by_email
          ? `Aktiv durch ${customerImpersonation.by_name || customerImpersonation.by_email} · schreibgeschützt`
          : "Diese Ansicht ist schreibgeschützt.",
        exitLabel: "Ansicht beenden",
        onExit: exitCustomer,
      };
    }
    if (isImpersonatingConcierge) {
      return {
        kind: "concierge",
        title: activeConciergeName
          ? `Concierge-Ansicht · ${activeConciergeName}`
          : "Concierge-Ansicht",
        subtitle: otherConciergeUnlocked
          ? "Bearbeitung freigeschaltet — Ihre Änderungen greifen sofort."
          : "Sie sehen, was dieser Concierge sieht — für Änderungen bitte PIN eingeben.",
        exitLabel: "Zurück zu Admin",
        onExit: exitConcierge,
      };
    }
    return null;
  }, [isImpersonatingCustomer, isImpersonatingConcierge, activeConciergeName, customerImpersonation, user, otherConciergeUnlocked, exitConcierge, exitCustomer]);

  if (!payload) return null;

  const goldGradient = "linear-gradient(90deg, rgba(201,169,97,0.94), rgba(201,169,97,0.82))";

  return (
    <div
      data-testid={`impersonation-banner-${payload.kind}`}
      className="sticky top-0 z-[45] w-full text-navy shadow-md"
      style={{ background: goldGradient, backdropFilter: "blur(4px)" }}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-2 flex items-center gap-3">
        <div className="flex items-center gap-2 shrink-0">
          <Eye className="w-4 h-4" strokeWidth={2} />
          <span className="label-caps text-[10.5px] md:text-[11px] whitespace-nowrap">
            {payload.kind === "customer" ? "Read-only" : "Impersonation"}
          </span>
        </div>
        <div className="flex-1 min-w-0 leading-tight">
          <div
            className="font-medium text-[13px] md:text-[13.5px] truncate"
            data-testid={`impersonation-banner-title-${payload.kind}`}
          >
            {payload.title}
          </div>
          <div className="text-[11px] md:text-[11.5px] text-navy/80 truncate">
            {payload.subtitle}
          </div>
        </div>
        <button
          type="button"
          onClick={payload.onExit}
          data-testid={`impersonation-banner-exit-${payload.kind}`}
          className="shrink-0 inline-flex items-center gap-1.5 bg-navy text-ivory hover:bg-navy-dark transition-colors text-[11px] md:text-[12px] tracking-[0.12em] uppercase px-3 py-1.5 rounded-full"
        >
          {payload.kind === "customer" ? (
            <LogOut className="w-3.5 h-3.5" strokeWidth={2} />
          ) : (
            <X className="w-3.5 h-3.5" strokeWidth={2.2} />
          )}
          <span className="hidden sm:inline">{payload.exitLabel}</span>
          <span className="sm:hidden">Beenden</span>
        </button>
        {payload.kind === "concierge" && (
          otherConciergeUnlocked ? (
            <button
              type="button"
              onClick={lockOtherConcierge}
              data-testid="impersonation-banner-relock"
              className="shrink-0 inline-flex items-center gap-1.5 bg-white/90 hover:bg-white text-navy transition-colors text-[11px] md:text-[12px] tracking-[0.12em] uppercase px-3 py-1.5 rounded-full border border-navy/25"
              title="Bearbeitung wieder sperren"
            >
              <Unlock className="w-3.5 h-3.5" strokeWidth={2} />
              <span className="hidden sm:inline">Wieder sperren</span>
              <span className="sm:hidden">Sperren</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={requestPinPrompt}
              data-testid="impersonation-banner-unlock"
              className="shrink-0 inline-flex items-center gap-1.5 bg-navy text-ivory hover:bg-navy-dark transition-colors text-[11px] md:text-[12px] tracking-[0.12em] uppercase px-3 py-1.5 rounded-full border border-navy"
              title="Fremd-Ansicht mit 4-stelliger PIN freischalten"
            >
              <KeyRound className="w-3.5 h-3.5" strokeWidth={2} />
              <span className="hidden sm:inline">PIN eingeben</span>
              <span className="sm:hidden">PIN</span>
            </button>
          )
        )}
      </div>
    </div>
  );
}
